import { Immutable } from "immer";
import React from "react";
import { ProductGridSSR } from "tsi-common-react/src/apps/product-grid2/ProductGridSSR/index";

import { templates } from "@reactivated";
import type { ProductTable as ProductTableValue } from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { AdvertSnippetBlock } from "../../blocks/AdvertSnippetBlock";
import { MattressBaseAdvert } from "../../blocks/MattressBaseAdvert";
import { MattressFoundationAdvert } from "../../blocks/MattressFoundationAdvert";
import { ProductAdvert } from "../../blocks/ProductAdvert";
import { ProductCallout } from "../../blocks/ProductCallout";
import { ProductHeroBanner } from "../../blocks/ProductHeroBanner";
import { ProductList } from "../../blocks/ProductList";
import { ProductReviewBlock } from "../../blocks/ProductReviewBlock";
import { ProductReviewForm } from "../../blocks/ProductReviewForm";
import { SealyProductTable } from "../../blocks/SealyProductTable";
import { StoreLocator } from "../../blocks/StoreLocator";
import { toolboxComponentsMap } from "../../blocks/toolboxComponentsMap";

/**
 * sealy ProductPage body assembly (#33585): dispatches every
 * ``ProductPageBodyContent`` block (the 30 Toolbox keys + 12 page-specific
 * keys) in one React render tree.
 *
 * FAITHFUL port of the legacy ``cms/product_page.html`` body render (verified
 * at git dev): a bare ``{% for block in self.body %}{% include_block block %}
 * {% endfor %}`` loop -- NO per-block Wagtail ``w-block-*`` wrapper,
 * reproduced with ``tagName={React.Fragment}`` (same as the
 * Home/Information assemblies).
 *
 * Every key dispatches natively -- NO legacy pre-rendered HTML string keys
 * remain. The 3 value-less StaticBlocks
 * (``mattress_base_advert`` / ``mattress_foundation_advert`` /
 * ``store_locator``) are ported components (#33285 batch A), ``hero_banner``
 * (``ProductHeroBanner``) is a ported StructBlock (#33285 batch B),
 * ``advert_snippet`` / ``product_list`` are ported components (#33285
 * batch C), and the 4 special-integration blocks are ported components
 * (#33285 batch D):
 *   - ``product_advert`` -> ``ProductAdvert``. The legacy template nested an
 *     ``include_react`` island (the tooltips); the tooltips now render
 *     directly in the component tree, collapsing the island host div +
 *     props script.
 *   - ``value_prop_advert`` -> ``ProductCallout`` (the legacy ``slugurl``
 *     lookups arrive resolved in the serialized value).
 *   - ``product_reviews_block`` -> ``ProductReviewBlock`` and
 *     ``product_reviews_form`` -> ``ProductReviewForm``: the legacy inline
 *     PowerReviews ``<script>`` pair became a post-hydration effect that
 *     loads ui.js once and calls ``POWERREVIEWS.display.render``.
 *
 * The 2 keys ported during the consolidation itself:
 *   - ``product_table`` -> ``SealyProductTable`` (the standard consolidation
 *     island-collapse).
 *   - ``product_grid`` -> ``ProductGridSSR`` (the stearns map precedent;
 *     mirrors the sealy ``ProductGridBlockTemplate.tsx`` re-export). The
 *     serialized envelope is null when no snippet is chosen or no request
 *     was available, in which case nothing renders (matching the legacy
 *     template's behavior of skipping client-side rendering).
 */
export const productPageComponentsMap = {
    ...toolboxComponentsMap,
    hero_banner: ProductHeroBanner,
    product_advert: ProductAdvert,
    mattress_base_advert: MattressBaseAdvert,
    advert_snippet: AdvertSnippetBlock,
    mattress_foundation_advert: MattressFoundationAdvert,
    product_list: ProductList,
    value_prop_advert: ProductCallout,
    product_reviews_block: ProductReviewBlock,
    product_reviews_form: ProductReviewForm,
    store_locator: StoreLocator,
    product_table: (block: { value: Immutable<ProductTableValue> }) => (
        <SealyProductTable value={block.value} />
    ),
    product_grid: (block: {
        value: Immutable<templates.ProductGridBlockTemplate> | null;
    }) => (block.value ? <ProductGridSSR {...block.value} /> : null),
} as const;

export const ProductPageBody = (
    props: Immutable<{ page: templates.ProductPageTemplate["page"] }>,
): React.JSX.Element => {
    // `body` is non-null (the model field is `blank=True` without
    // `null=True`); an empty stream renders nothing.
    return (
        <StreamField
            tagName={React.Fragment}
            value={props.page.body.value}
            components={productPageComponentsMap}
        />
    );
};
